import { Banner, Field, Select } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormPage, FormSection } from '../../components/ui/FormPage';
import { PasswordField } from '../../components/ui/PasswordField';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import type { ToolsetDetail } from './toolCatalogShared';
import { PageSkeleton } from '../../components/ui/Skeleton';

/** IA › Ferramentas › <integração> › Instalar: ambiente e credencial, numa página de formulário */
export function AiToolInstallPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [t, setT] = useState<ToolsetDetail | null>(null);
  const [vaultReady, setVaultReady] = useState(true);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await fetchApi(`/api/ai/tool-catalog/${slug}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { navigate('/ia/ferramentas', { replace: true }); return; }
      if (j.toolset.installed) { navigate(`/ia/ferramentas/${slug}`, { replace: true }); return; }
      setT(j.toolset); setVaultReady(j.vaultReady);
      setInputs(Object.fromEntries((j.toolset.inputs as ToolsetDetail['inputs']).map((f) => [f.key, f.default ?? f.options?.[0] ?? ''])));
    })();
  }, [fetchApi, slug, navigate]);

  if (!t) return <PageSkeleton label="Carregando ferramenta" />;
  const needsVault = t.needsCredential && !vaultReady;

  const install = async () => {
    setBusy(true);
    const r = await fetchApi(`/api/ai/tool-catalog/${slug}/install`, { method: 'POST', body: JSON.stringify({ inputs, secret: secret.trim() || undefined }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível instalar'); return; }
    toast.success(`${t.name} instalada. Ligue as ferramentas no agente em IA › Agentes.`);
    navigate(`/ia/ferramentas/${slug}`);
  };

  return (
    <FormPage
      backTo={`/ia/ferramentas/${slug}`}
      backLabel={t.name}
      title={`Instalar ${t.name}`}
      description={`${t.toolCount} ${t.toolCount === 1 ? 'ferramenta fica disponível' : 'ferramentas ficam disponíveis'} para os agentes da organização.`}
      submitLabel="Instalar"
      submitting={busy}
      submitDisabled={needsVault || (t.needsCredential && !secret.trim())}
      onSubmit={install}
      footerNote="Você escolhe depois, em cada agente, quais ferramentas ele pode usar."
    >
      {needsVault && <Banner variant="alert" description="O cofre de credenciais não está configurado neste ambiente. Peça ao administrador da plataforma." />}
      {t.inputs.length > 0 && (
        <FormSection title="Ambiente">
          {t.inputs.map((f) => (
            <Field key={f.key} label={f.label}>
              <Select value={inputs[f.key] ?? null} items={(f.options ?? []).map((o) => ({ value: o, label: o }))} onValueChange={(v: string | null) => v && setInputs((x) => ({ ...x, [f.key]: v }))} aria-label={f.label} />
            </Field>
          ))}
        </FormSection>
      )}
      {t.needsCredential && t.secret && (
        <FormSection title="Credencial" description={t.secret.help}>
          <PasswordField label={t.secret.label} value={secret} onChange={setSecret} autoComplete="off" placeholder="Cole aqui" />
          <p className="text-sm text-kumo-subtle">Fica cifrada no cofre da organização. Depois de salva, não aparece mais, só os últimos caracteres.</p>
        </FormSection>
      )}
      {!t.needsCredential && t.inputs.length === 0 && (
        <FormSection title="Pronto para instalar"><p className="text-kumo-subtle">Esta API é aberta: não há nada para configurar.</p></FormSection>
      )}
    </FormPage>
  );
}
